import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapIcon, ListIcon } from 'lucide-react';
import { KINDS, PLACES, CITIES, Kind } from '../../data/smaken';
import { Reveal } from '../Reveal';
import { Note } from '../Marks';
import { SMALAND_PATH } from '../SmalandMap';
const accentFor = (kind: Kind) => KINDS.find(k => k.id === kind)?.accent ?? '#111111';
export function MapSection() {
  const [filter, setFilter] = useState<Kind | 'alla'>('alla');
  const [active, setActive] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'lista' | 'karta'>('lista');
  const visible = filter === 'alla' ? PLACES : PLACES.filter(p => p.kind === filter);
  const filters: {
    id: Kind | 'alla';
    label: string;
  }[] = [{
    id: 'alla',
    label: 'Alla'
  }, ...KINDS.map(k => ({
    id: k.id,
    label: k.label.charAt(0) + k.label.slice(1).toLowerCase()
  }))];
  const map = <div className="relative bg-papper" data-id="element-501" data-mp="goyu8ng9a6xg">
      <svg viewBox="0 0 100 100" className="h-full w-full" role="img" aria-label="Karta över Småland" data-id="element-502" data-mp="gs4ftspoeqof">
        <path d={SMALAND_PATH} fill="#EDE6DA" stroke="#111111" strokeWidth="0.6" strokeLinejoin="round" data-id="element-503" data-mp="1ibod2u7cyqgt" />

        {visible.map(place => {
        const pos = CITIES[place.city as keyof typeof CITIES];
        if (!pos) return null;
        const isActive = active === place.slug;
        return <g key={place.slug} data-id="element-504" data-mp="zf6cdnaj7g8f">
              <circle cx={pos.x} cy={pos.y} r={isActive ? 2.6 : 1.5} fill={accentFor(place.kind)} stroke="#F7F3EC" strokeWidth="0.5" className="cursor-pointer transition-[r] duration-150 ease-out" onMouseEnter={() => setActive(place.slug)} onMouseLeave={() => setActive(null)} data-id="element-505" data-mp="zg7ziq4tnt6i" />
              {isActive && <text x={pos.x} y={pos.y - 4} textAnchor="middle" className="pointer-events-none" style={{
            font: '600 3px Inter, sans-serif',
            fill: '#111111'
          }} data-id="element-506" data-mp="bpvj0qvigo4b">
                  {place.name}
                </text>}
            </g>;
      })}
      </svg>

      <Note className="absolute bottom-3 right-4 !text-black/45" data-id="element-507" data-mp="3obkgflu3rz1w">{visible.length} ställen</Note>
    </div>;
  return <section aria-labelledby="karta-rubrik" className="border-t border-black/12" data-id="element-508" data-mp="krxuy4qmqeo0">
      <div className="mx-auto max-w-page px-5 py-16 md:px-8 md:py-24" data-id="element-509" data-mp="prkj397zxfbl">
        <Reveal data-id="element-510" data-mp="pqslxtyr39j7">
          <h2 id="karta-rubrik" className="display-xl text-5xl uppercase sm:text-7xl" data-id="element-511" data-mp="ptetvupjzr1y">
            Var finns det goda?
          </h2>

          <div className="mt-8 flex flex-wrap items-center gap-2" data-id="element-512" data-mp="g0zdh1twnaj6">
            {filters.map(f => <button key={f.id} onClick={() => setFilter(f.id)} aria-pressed={filter === f.id} className={`border px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out ${filter === f.id ? 'border-black bg-black text-papper' : 'border-black/20 text-black hover:border-black'}`} data-id="element-513" data-mp="13r3d6y3z2q4w">
                {f.label}
              </button>)}

            <button onClick={() => setMobileView(v => v === 'lista' ? 'karta' : 'lista')} className="ml-auto flex items-center gap-2 border border-black/20 px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:border-black lg:hidden" data-id="element-514" data-mp="2xzz2r0bdup02">
              {mobileView === 'lista' ? <>
                  <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-515" data-mp="2f7eha3y0jm6a" /> Karta
                </> : <>
                  <ListIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-516" data-mp="3qo4qlvxqgu44" /> Lista
                </>}
            </button>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr),minmax(0,1fr)] lg:gap-14" data-id="element-517" data-mp="pugjac415w9j">
          {/* Map — hidden on mobile unless toggled, since the list is the faster way to browse. */}
          <div className={`${mobileView === 'karta' ? 'block' : 'hidden'} lg:block`} data-id="element-518" data-mp="ys0m3phjk0m9">
            <div className="border border-black/12 lg:sticky lg:top-24" data-id="element-519" data-mp="13tzn4spt6lg1">{map}</div>
          </div>

          <ul className={`${mobileView === 'lista' ? 'block' : 'hidden'} border-t border-black/15 lg:block`} data-id="element-520" data-mp="18qzs10ax9i5k">
            {visible.map(place => <li key={place.slug} data-id="element-521" data-mp="1hk30svamh6tv">
                <Link to={`/plats/${place.slug}`} onMouseEnter={() => setActive(place.slug)} onMouseLeave={() => setActive(null)} className={`flex items-baseline justify-between gap-4 border-b border-black/15 py-4 transition-colors duration-150 ease-out ${active === place.slug ? 'text-rod' : 'hover:text-rod'}`} data-id="element-522" data-mp="1hl4ez8mxq9ft">
                  <span className="flex items-baseline gap-3" data-id="element-523" data-mp="3c01i7nsxmmih">
                    <span className="h-2 w-2 shrink-0 translate-y-[-1px] rounded-full" style={{
                  backgroundColor: accentFor(place.kind)
                }} aria-hidden="true" data-id="element-524" data-mp="3bygp82m9nbmq" />
                    <span className="font-display text-xl uppercase" data-id="element-525" data-mp="3jymmgf3goai">{place.name}</span>
                  </span>
                  <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide2 text-black/45" data-id="element-526" data-mp="3upzqo50n6hmn">
                    {place.city}
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}